import { Checkbox, Flex, Form, Input, Modal } from "antd";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { AddUserForm } from "../types";

import "./AddUserModal.scss";

type Props = {
  onAdd: (userData: AddUserForm) => Promise<unknown>;
  onClose: () => void;
  isAddUserInProgress: boolean;
  isOpen: boolean;
};

const ModalTitle = () => (
  <>
    <span className="permissions-add-user-modal-title">
      <FormattedMessage id="permissions.user.actions.add.title" />
    </span>
    <p className="permissions-add-user-modal-subtitle">
      <FormattedMessage id="permissions.user.actions.add.subtitle" />
    </p>
  </>
);

const AddUserModal = ({ onAdd, isOpen, onClose, isAddUserInProgress }: Props) => {
  const { formatMessage } = useIntl();
  const [resetAfterSubmit, setResetAfterSubmit] = useState(false);
  const [form] = Form.useForm<AddUserForm>();

  const handleSubmit = (values) =>
    onAdd(values).then(() => {
      if (resetAfterSubmit) {
        form.resetFields();
        setResetAfterSubmit(false);
      } else {
        onClose();
      }
    });

  return (
    <Modal
      className="permissions-add-user-modal"
      open={isOpen}
      width={400}
      centered
      onCancel={onClose}
      title={<ModalTitle />}
      okText={formatMessage({ id: "invite" })}
      okButtonProps={{ loading: isAddUserInProgress, htmlType: "submit" }}
      modalRender={(dom) => (
        <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} clearOnDestroy onFinish={handleSubmit}>
          {dom}
        </Form>
      )}
      footer={(originNode) => (
        <Flex align="center" justify="space-between">
          <Checkbox checked={resetAfterSubmit} onChange={(e) => setResetAfterSubmit(e.target.checked)}>
            <FormattedMessage id="permissions.user.actions.add.another" />
          </Checkbox>
          <Flex align="center" gap={8}>
            {originNode}
          </Flex>
        </Flex>
      )}
      destroyOnHidden
    >
      <Form.Item<AddUserForm>
        required
        name="name"
        label={formatMessage({ id: "name" })}
        tooltip={formatMessage({ id: "form.help.permissions.user.add.name" })}
        rules={[
          {
            required: true,
            message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "name" }) }),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item<AddUserForm>
        required
        name="email"
        label={formatMessage({ id: "email" })}
        tooltip={formatMessage({ id: "form.help.permissions.user.add.email" })}
        rules={[
          {
            type: "email",
            message: formatMessage({
              id: "form.help.permissions.user.add.email.invalid",
            }),
          },
          {
            required: true,
            message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "email" }) }),
          },
        ]}
      >
        <Input />
      </Form.Item>
    </Modal>
  );
};

export default AddUserModal;
